import React, { useState } from 'react';
import axios from 'axios';
import MainLayout from '../../components/Layout/MainLayout';
import {
  PageCard, BtnPrimary, BtnSecondary,
  FormField, FormInput, FormSelect, Alert
} from '../../components/UI/UIComponents';
import './CreateMeeting.css';

const API_BASE = process.env.REACT_APP_API_URL || `http://localhost:${process.env.REACT_APP_PORT || 5001}`;
const CLIENT_URL = process.env.REACT_APP_CLIENT_URL || `http://localhost:${process.env.REACT_APP_CLIENT_PORT || 3006}`;

const LinkIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>;
const UserIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const GlobeIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>;

const FEATURE_LIST = [
  { icon: '🎤', title: 'Real-time Speech Recognition',  desc: 'Browser-native speech-to-text in EN/JA' },
  { icon: '🌐', title: 'AI Translation Engine',          desc: 'Google-powered EN ↔ JA translation' },
  { icon: '🔊', title: 'Text-to-Speech Playback',       desc: 'Translated audio played to participants' },
  { icon: '📋', title: 'Live Transcript Panel',          desc: 'Full timestamped transcript per session' },
];

const LANGUAGES = [
  { value: 'en-US',  label: 'English',    flag: '🇺🇸', sub: 'I speak English',    native: 'English'    },
  { value: 'ja-JP',  label: 'Japanese',   flag: '🇯🇵', sub: '日本語で話します',     native: '日本語'       },
  { value: 'zh-CN',  label: 'Chinese',    flag: '🇨🇳', sub: '我说普通话',          native: '中文'         },
  { value: 'vi-VN',  label: 'Vietnamese', flag: '🇻🇳', sub: 'Tôi nói tiếng Việt', native: 'Tiếng Việt'  },
  { value: 'bn-BD',  label: 'Bangla',      flag: '🇧🇩', sub: 'আমি বাংলায় কথা বলি',  native: 'বাংলা'         },
]

const CreateMeeting = () => {
  const [name,     setName]     = useState('');
  const [language, setLanguage] = useState('en-US');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [errors,   setErrors]   = useState({});

  const validate = () => {
    const e = {};
    if (!name.trim())     e.name = 'Please enter your display name.';
    if (!language)        e.language = 'Please select your language.';
    return e;
  };

  const handleCreate = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/meeting`, { email: 'rukucse11@gmail.com' });
      const joinUrl = res.data.join_url;
      if (!joinUrl) throw new Error('No join URL received from server.');
      const newUrl = joinUrl.replace(
        'https://us05web.zoom.us/j/',
        `${CLIENT_URL}?`
      ) + `?role=1?name=${encodeURIComponent(name.trim())}?lang=${language}?room=development`;
      window.location.replace(newUrl);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to create meeting. Please check the server and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout pageTitle="Create Meeting" pageSubtitle="Start a new AI-interpreted Zoom session">
      <div className="create-meeting">

        {/* Left column — Form */}
        <div className="cm-form-col">
          <PageCard title="Meeting Setup" subtitle="Configure your session details">

            {error && <Alert type="error">{error}</Alert>}

            <FormField label="Display Name" error={errors.name} required>
              <FormInput
                icon={<UserIcon/>}
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={e => { setName(e.target.value); setErrors(p => ({...p, name: ''})); }}
                maxLength={40}
              />
            </FormField>

            <FormField label="Your Speaking Language" error={errors.language} required>
              <div className="cm-lang-cards">
                {LANGUAGES.map(l => (
                  <button
                    key={l.value}
                    type="button"
                    className={`cm-lang-card${language === l.value ? ' cm-lang-card--active' : ''}`}
                    onClick={() => { setLanguage(l.value); setErrors(p => ({...p, language: ''})); }}
                  >
                    <span className="cm-lang-flag">{l.flag}</span>
                    <span className="cm-lang-card-name">{l.native}</span>
                    <span className="cm-lang-card-sub">{l.sub}</span>
                    {language === l.value && <span className="cm-lang-check">✓</span>}
                  </button>
                ))}
              </div>
              <p className="cm-lang-hint">
                🎙 {LANGUAGES.find(l=>l.value===language)?.label || 'Selected language'} → others hear their own language
              </p>
            </FormField>

            <div className="cm-actions">
              <BtnPrimary onClick={handleCreate} loading={loading} fullWidth>
                {loading ? '' : '🚀  Create Meeting Now'}
              </BtnPrimary>
              <BtnSecondary onClick={() => window.history.back()} fullWidth>
                Cancel
              </BtnSecondary>
            </div>

            <p className="cm-disclaimer">
              A Zoom meeting will be created and you'll be redirected automatically. Make sure your microphone is allowed.
            </p>
          </PageCard>
        </div>

        {/* Right column — Info */}
        <div className="cm-info-col">
          <PageCard title="What you get" subtitle="AI-powered interpretation features">
            <div className="cm-feature-list">
              {FEATURE_LIST.map((f, i) => (
                <div className="cm-feature-item" key={i}>
                  <div className="cm-feature-icon">{f.icon}</div>
                  <div>
                    <p className="cm-feature-title">{f.title}</p>
                    <p className="cm-feature-desc">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </PageCard>

          {/* Flow diagram */}
          <PageCard title="How it works">
            <div className="cm-flow">
              {['Speak naturally', 'AI recognizes speech', 'Translates instantly', 'Participant hears'].map((step, i) => (
                <React.Fragment key={i}>
                  <div className="cm-flow-step">
                    <div className="cm-flow-num">{i+1}</div>
                    <span>{step}</span>
                  </div>
                  {i < 3 && <div className="cm-flow-arrow">↓</div>}
                </React.Fragment>
              ))}
            </div>
          </PageCard>
        </div>

      </div>
    </MainLayout>
  );
};

export default CreateMeeting;
